import { useCallback, useEffect, useState } from 'react';
import type { CreateCustomerRequest, CustomerDTO, Page } from '@six-pockets/shared';
import { ApiRequestError, customersApi } from '@/api';
import { PlusIcon, SearchIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, ConfirmModal, InlineAlert, Pagination, TextField } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { CustomerFormModal } from '@/features/customers/components/CustomerFormModal';
import { CustomerRow } from '@/features/customers/components/CustomerRow';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import styles from '@/styles/pages/CustomersPage/style.scss';

const PAGE_SIZE = 12;

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/** "Manoj already has this mobile number" when the server returns the existing customer with its 409. */
function saveErrorOf(err: unknown) {
  if (err instanceof ApiRequestError && err.status === 409 && err.details) {
    return `${(err.details as CustomerDTO).name} already has this mobile number`;
  }
  return messageOf(err);
}

/**
 * Every customer, A to Z, with search (name or mobile) and paging done by the server. Add and Edit for anyone with
 * the page; Delete for admin only (the settings module), refused by the server once a customer is on a bill.
 * Later: a customer's bookings, bills, payments and credits.
 */
export function CustomersPage() {
  const canDelete = useAuth().can('settings');
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const [result, setResult] = useState<Page<CustomerDTO> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** The customer in the form modal: 'new' to add one. */
  const [editing, setEditing] = useState<CustomerDTO | 'new' | null>(null);
  const [deleting, setDeleting] = useState<CustomerDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // A new search goes back to the first page.
  useEffect(() => setPage(1), [query]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    customersApi.list({ q: query || undefined, page, pageSize: PAGE_SIZE }, ctrl.signal).then(
      (r) => {
        setResult(r);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [query, page, reloadKey]);

  // Deleting the last customer on a page leaves it empty: step back a page.
  useEffect(() => {
    if (result && result.items.length === 0 && result.page > 1) setPage(result.page - 1);
  }, [result]);

  const save = async (body: CreateCustomerRequest) => {
    setBusy(true);
    setModalError(null);
    try {
      if (editing && editing !== 'new') {
        await customersApi.update(editing.id, body);
      } else {
        await customersApi.create(body);
        // Show the new customer: search their number (the list is A to Z, so they could be on any page).
        setSearch(body.phone);
      }
      setEditing(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setModalError(saveErrorOf(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    setModalError(null);
    try {
      await customersApi.remove(deleting.id);
      setDeleting(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setModalError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (busy) return;
    setEditing(null);
    setDeleting(null);
    setModalError(null);
  };

  // Stable, so the memoised rows don't re-render.
  const startEdit = useCallback((c: CustomerDTO) => setEditing(c), []);
  const startDelete = useCallback((c: CustomerDTO) => setDeleting(c), []);

  const customers = result?.items ?? [];
  const total = result?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Customers"
        aside={
          <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => setEditing('new')}>
            Add customer
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <span className={styles.count}>{result && `${total} ${total === 1 ? 'customer' : 'customers'}${query ? ' found' : ''}`}</span>
        <TextField
          size="md"
          aria-label="Search customers by name or mobile"
          placeholder="Search name or mobile"
          prefix={<SearchIcon size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.search}
        />
      </div>

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading customers…</p>
      ) : customers.length === 0 ? (
        <p className={styles.empty}>{query ? `No customers match "${query}"` : 'No customers yet'}</p>
      ) : (
        <ul className={styles.list} aria-busy={loading}>
          {customers.map((c) => (
            <CustomerRow key={c.id} customer={c} onEdit={startEdit} onDelete={canDelete ? startDelete : undefined} />
          ))}
        </ul>
      )}

      {result && <Pagination page={result.page} pageSize={result.pageSize} total={result.total} onPageChange={setPage} />}

      {editing && (
        <CustomerFormModal
          key={editing === 'new' ? 'new' : editing.id}
          customer={editing === 'new' ? null : editing}
          busy={busy}
          error={modalError}
          onSave={(body) => void save(body)}
          onClose={closeModal}
        />
      )}

      <ConfirmModal
        open={!!deleting}
        title="Delete customer"
        message={<>Are you sure you want to delete <strong>{deleting?.name}</strong>?</>}
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={busy}
        error={modalError}
        onConfirm={() => void confirmDelete()}
        onCancel={closeModal}
      />
    </>
  );
}
